import { useState } from "react";
import toast from "react-hot-toast";
import { X, Plus, Trash2 } from "lucide-react";
import { useCreateInvoice, InvoiceLinePayload } from "@/api/invoices";
import { useClients, displayName } from "@/api/clients";
import { getApiErrorMessage } from "@/api/client";

interface LineDraft {
  description: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
}

const emptyLine = (): LineDraft => ({ description: "", quantity: "1", unitPrice: "", taxRate: "19" });

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

export default function CreateInvoiceModal({ clientId: fixedClientId, onClose }: { clientId?: string; onClose: () => void }) {
  const createInvoice = useCreateInvoice();
  const [issueDate, setIssueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);

  const { data: clientResults } = useClients({ search: clientSearch, pageSize: 6 });
  const effectiveClientId = fixedClientId ?? selectedClient?.id;

  const updateLine = (index: number, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const computeLine = (l: LineDraft) => {
    const subtotal = (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0);
    const tax = subtotal * ((Number(l.taxRate) || 0) / 100);
    return { subtotal, tax, total: subtotal + tax };
  };

  const totals = lines.reduce(
    (acc, l) => {
      const c = computeLine(l);
      return { subtotal: acc.subtotal + c.subtotal, tax: acc.tax + c.tax, total: acc.total + c.total };
    },
    { subtotal: 0, tax: 0, total: 0 }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveClientId) {
      toast.error("Debe seleccionar un cliente.");
      return;
    }
    const payloadLines: InvoiceLinePayload[] = lines
      .filter((l) => l.description && l.unitPrice)
      .map((l) => ({
        description: l.description,
        quantity: l.quantity || "1",
        unitPrice: l.unitPrice,
        taxRate: l.taxRate || "0",
      }));
    if (payloadLines.length === 0) {
      toast.error("Agregue al menos un ítem con descripción y precio.");
      return;
    }
    try {
      await createInvoice.mutateAsync({
        clientId: effectiveClientId,
        issueDate,
        dueDate,
        notes: notes || undefined,
        lines: payloadLines,
      });
      toast.success("Factura creada correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Nueva factura</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          {!fixedClientId && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Cliente</label>
              {selectedClient ? (
                <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                  <span>{selectedClient.name}</span>
                  <button type="button" className="text-xs text-gray-400 hover:text-red-600" onClick={() => setSelectedClient(null)}>
                    Quitar
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    className="input-field mt-1"
                    placeholder="Buscar cliente..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    required={!selectedClient}
                  />
                  {clientSearch && clientResults?.data && clientResults.data.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                      {clientResults.data.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                          onClick={() => {
                            setSelectedClient({ id: c.id, name: displayName(c) });
                            setClientSearch("");
                          }}
                        >
                          {displayName(c)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Fecha de expedición</label>
              <input type="date" className="input-field mt-1" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Fecha de vencimiento</label>
              <input type="date" className="input-field mt-1" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Notas (opcional)</label>
            <textarea className="input-field mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">Ítems</label>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
                onClick={() => setLines((prev) => [...prev, emptyLine()])}
              >
                <Plus size={12} /> Agregar ítem
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {lines.map((line, index) => {
                const c = computeLine(line);
                return (
                  <div key={index} className="rounded-md border border-gray-100 p-2">
                    <div className="flex items-center gap-2">
                      <input
                        className="input-field flex-1 text-xs"
                        placeholder="Descripción del ítem"
                        value={line.description}
                        onChange={(e) => updateLine(index, { description: e.target.value })}
                        required
                      />
                      {lines.length > 1 && (
                        <button
                          type="button"
                          className="text-gray-400 hover:text-red-600"
                          onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                          aria-label="Quitar ítem"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="Cantidad"
                        className="input-field w-24 text-xs"
                        value={line.quantity}
                        onChange={(e) => updateLine(index, { quantity: e.target.value })}
                      />
                      <input
                        type="number"
                        min="0"
                        placeholder="Precio unitario"
                        className="input-field w-32 text-xs"
                        value={line.unitPrice}
                        onChange={(e) => updateLine(index, { unitPrice: e.target.value })}
                        required
                      />
                      <input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="% Impuesto"
                        className="input-field w-24 text-xs"
                        value={line.taxRate}
                        onChange={(e) => updateLine(index, { taxRate: e.target.value })}
                      />
                      <span className="ml-auto text-xs font-medium text-gray-600">{currency.format(c.total)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex justify-end gap-4 text-xs font-medium text-gray-600">
              <span>Subtotal: {currency.format(totals.subtotal)}</span>
              <span>Impuesto: {currency.format(totals.tax)}</span>
              <span className="text-gray-900">Total: {currency.format(totals.total)}</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createInvoice.isPending}>
              {createInvoice.isPending ? "Creando..." : "Crear factura"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
